import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import { FileBarChart2, Loader2, Printer } from "lucide-react";

const ayBasi = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-01`;
};
const bugunStr = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" });

const YER_RENK = {
  "Kasa": "text-emerald-700",
  "Banka": "text-sky-700",
  "Yönetim Tahsilatı": "text-violet-700",
  "Tedarikçi Ödemesi": "text-amber-700",
};

export const TahsilatRaporu = () => {
  const [baslangic, setBaslangic] = useState(ayBasi());
  const [bitis, setBitis] = useState(bugunStr());
  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/ciro/tahsilat-rapor", { params: { baslangic, bitis } });
      setRapor(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Tahsilat raporu yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [baslangic, bitis]);

  useEffect(() => {
    fetchRapor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rounded-xl border bg-card p-4 space-y-4" data-testid="tahsilat-raporu">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #tahsilat-rapor-yazdir, #tahsilat-rapor-yazdir * { visibility: visible !important; }
          #tahsilat-rapor-yazdir { position: absolute; inset: 0; padding: 24px; background: white; }
          #tahsilat-rapor-yazdir .break-inside-avoid, #tahsilat-rapor-yazdir table { break-inside: avoid; }
        }
      `}</style>
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <div className="flex items-center gap-2">
          <FileBarChart2 className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-display text-sm font-semibold">Tahsilat Raporu</h3>
        </div>
        <div className="ml-auto flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Başlangıç</Label>
            <Input type="date" value={baslangic} onChange={(e) => setBaslangic(e.target.value)} className="h-10 w-40" data-testid="tahsilat-rapor-baslangic" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Bitiş</Label>
            <Input type="date" value={bitis} onChange={(e) => setBitis(e.target.value)} className="h-10 w-40" data-testid="tahsilat-rapor-bitis" />
          </div>
          <Button onClick={fetchRapor} disabled={loading} className="h-10 gap-1.5" data-testid="tahsilat-rapor-getir-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileBarChart2 className="h-4 w-4" />} Raporu Getir
          </Button>
          <Button variant="outline" onClick={() => window.print()} disabled={!rapor} className="h-10 gap-1.5" data-testid="tahsilat-rapor-yazdir-button">
            <Printer className="h-4 w-4" /> Yazdır / PDF Kaydet
          </Button>
        </div>
      </div>

      {loading && !rapor ? (
        <Skeleton className="h-40" />
      ) : !rapor ? null : (
        <div id="tahsilat-rapor-yazdir" className="space-y-4">
          <div className="hidden print:flex items-baseline justify-between border-b pb-3">
            <div>
              <h2 className="font-display text-lg font-bold">3A Otomotiv — Tahsilat Raporu</h2>
              <p className="text-sm text-muted-foreground">{formatDate(baslangic)} – {formatDate(bitis)}</p>
            </div>
            <p className="text-xs text-muted-foreground">Rapor tarihi: {new Date().toLocaleDateString("tr-TR")}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tahsilat-rapor-ozet">
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Toplam Tahsilat</p>
              <p className="mt-1 font-display text-lg font-bold tabular-nums" data-testid="tahsilat-rapor-toplam">{formatTL(rapor.toplam)}</p>
              <p className="text-[10px] text-muted-foreground">{rapor.adet} işlem</p>
            </div>
            {rapor.yerler.map((y) => (
              <div key={y.odeme_yeri} className="rounded-lg border bg-muted/30 p-3" data-testid={`tahsilat-rapor-yer-${y.odeme_yeri}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{y.odeme_yeri}</p>
                <p className={`mt-1 font-display text-lg font-bold tabular-nums ${YER_RENK[y.odeme_yeri] || ""}`}>{formatTL(y.toplam)}</p>
                <p className="text-[10px] text-muted-foreground">{y.adet} işlem</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-lg border overflow-hidden">
              <p className="bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Kişi Bazında (Tahsilatı Alan)</p>
              {rapor.kisiler.length === 0 ? (
                <p className="px-3 py-3 text-sm text-muted-foreground">Kayıt yok.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody data-testid="tahsilat-rapor-kisiler">
                    {rapor.kisiler.map((k) => (
                      <tr key={k.alan_kisi} className="border-t border-border/50">
                        <td className="px-3 py-2">{k.alan_kisi}</td>
                        <td className="px-3 py-2 text-right text-xs text-muted-foreground">{k.adet} işlem</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatTL(k.toplam)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="rounded-lg border overflow-hidden">
              <p className="bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Tedarikçi Ödemeleri</p>
              {rapor.tedarikciler.length === 0 ? (
                <p className="px-3 py-3 text-sm text-muted-foreground">Bu aralıkta tedarikçi ödemesi yok.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody data-testid="tahsilat-rapor-tedarikciler">
                    {rapor.tedarikciler.map((t) => (
                      <tr key={t.tedarikci} className="border-t border-border/50">
                        <td className="px-3 py-2">{t.tedarikci}</td>
                        <td className="px-3 py-2 text-right text-xs text-muted-foreground">{t.adet} işlem</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatTL(t.toplam)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="rounded-lg border overflow-x-auto">
            <p className="bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Tahsilat Hareketleri ({rapor.hareketler.length})
            </p>
            {rapor.hareketler.length === 0 ? (
              <p className="px-3 py-3 text-sm text-muted-foreground" data-testid="tahsilat-rapor-bos">Seçilen aralıkta tahsilat yok.</p>
            ) : (
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2">Tarih</th>
                    <th className="px-3 py-2">Belge / İş Emri</th>
                    <th className="px-3 py-2">Müşteri / Sigorta</th>
                    <th className="px-3 py-2">Alan Kişi</th>
                    <th className="px-3 py-2">Nereye Ödendi</th>
                    <th className="px-3 py-2 text-right">Tutar</th>
                  </tr>
                </thead>
                <tbody data-testid="tahsilat-rapor-hareketler">
                  {rapor.hareketler.map((h) => (
                    <tr key={h.id} className="border-t border-border/50">
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(h.tarih)}</td>
                      <td className="px-3 py-2 tabular-nums">{h.belge_no || h.is_emri_no || "—"}</td>
                      <td className="px-3 py-2 max-w-[200px] truncate">{h.musteri || h.sigorta_sirketi || "—"}</td>
                      <td className="px-3 py-2">{h.alan_kisi || "—"}</td>
                      <td className={`px-3 py-2 whitespace-nowrap ${YER_RENK[h.odeme_yeri] || ""}`}>
                        {h.odeme_yeri}{h.tedarikci ? ` → ${h.tedarikci}` : ""}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap">{formatTL(h.tutar)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
